// The answer script for beta testers (2026-11-04), in the four interface languages.
// Instructions are in the interface language; the answers to read and the
// interviewer's words stay in English (report language rule).

const EN = {
  button: "Answer script",
  buttonHint: "Answers to read aloud for each question, and what the interviewer should do next. Opens in a new tab.",
  title: "Answer script",
  intro: "Read the answer for each question aloud, in English, then check that the interviewer does what the script says. Each answer is written to test one thing (a complete answer, a missing result, a general answer…) at the English level shown.",
  englishLabel: "English level of the answers",
  english: { "": "Mixed", strong: "Strong", intermediate: "Intermediate", weak: "Weak" },
  level: { strong: "Strong English", intermediate: "Intermediate English", weak: "Weak English" },
  writeAgain: "Write a new script",
  print: "Print",
  writing: "Writing your script… this takes about 10–30 seconds.",
  writesLeft: (n) => (n === 1 ? "1 more new script for this interview." : `${n} more new scripts for this interview.`),
  question: (n) => `Question ${n}`,
  say: "Say",
  expect: "The interviewer should",
  expectNone: "move on to the next question (no follow-up).",
  expectRepeat: "ask you to repeat:",
  expectFollowUp: "ask a follow-up:",
  expectFollowUpAny: "ask a follow-up question.",
  note: "This answer may still touch the point. If no follow-up comes, try leaving it out more clearly.",
  followUpsOff: "Follow-up questions are switched off at the moment, so every answer will just move on.",
  cases: {
    complete: "Complete answer",
    no_result: "Leaves out how it turned out",
    hypothetical: "General answer, no real example",
    no_example: "Says there is no example",
    generic: "Generic, doesn't connect to this job",
    unclear: "Mumbled, hard to understand",
    missing: "Leaves out:",
  },
  errors: {
    answer_script_off: "The answer script isn't switched on for your account.",
    answer_script_limit: "You've written the most scripts allowed for this interview.",
    no_questions: "This interview's questions aren't ready yet. Try again in a moment.",
    other: "We couldn't write the script. Please try again.",
  },
};

const PT = {
  button: "Roteiro de respostas",
  buttonHint: "Respostas para ler em voz alta em cada pergunta e o que o entrevistador deve fazer em seguida. Abre em uma nova aba.",
  title: "Roteiro de respostas",
  intro: "Leia em voz alta, em inglês, a resposta de cada pergunta e verifique se o entrevistador faz o que o roteiro indica. Cada resposta foi escrita para testar uma coisa (resposta completa, sem resultado, resposta genérica…) no nível de inglês indicado.",
  englishLabel: "Nível de inglês das respostas",
  english: { "": "Misto", strong: "Avançado", intermediate: "Intermediário", weak: "Básico" },
  level: { strong: "Inglês avançado", intermediate: "Inglês intermediário", weak: "Inglês básico" },
  writeAgain: "Gerar um novo roteiro",
  print: "Imprimir",
  writing: "Gerando seu roteiro… isso leva cerca de 10–30 segundos.",
  writesLeft: (n) => (n === 1 ? "Mais 1 roteiro novo para esta entrevista." : `Mais ${n} roteiros novos para esta entrevista.`),
  question: (n) => `Pergunta ${n}`,
  say: "Diga",
  expect: "O entrevistador deve",
  expectNone: "passar para a próxima pergunta (sem pergunta de acompanhamento).",
  expectRepeat: "pedir que você repita:",
  expectFollowUp: "fazer uma pergunta de acompanhamento:",
  expectFollowUpAny: "fazer uma pergunta de acompanhamento.",
  note: "Esta resposta ainda pode mencionar o ponto. Se não vier nenhuma pergunta de acompanhamento, tente deixá-lo de fora com mais clareza.",
  followUpsOff: "As perguntas de acompanhamento estão desativadas no momento, então todas as respostas simplesmente passarão para a próxima pergunta.",
  cases: {
    complete: "Resposta completa",
    no_result: "Não diz qual foi o resultado",
    hypothetical: "Resposta genérica, sem exemplo real",
    no_example: "Diz que não tem um exemplo",
    generic: "Genérica, sem relação com esta vaga",
    unclear: "Murmurada, difícil de entender",
    missing: "Deixa de fora:",
  },
  errors: {
    answer_script_off: "O roteiro de respostas não está ativado para a sua conta.",
    answer_script_limit: "Você já gerou o número máximo de roteiros para esta entrevista.",
    no_questions: "As perguntas desta entrevista ainda não estão prontas. Tente novamente em instantes.",
    other: "Não foi possível gerar o roteiro. Tente novamente.",
  },
};

const ES = {
  button: "Guion de respuestas",
  buttonHint: "Respuestas para leer en voz alta en cada pregunta y lo que el entrevistador debe hacer después. Se abre en una nueva pestaña.",
  title: "Guion de respuestas",
  intro: "Lea en voz alta, en inglés, la respuesta de cada pregunta y verifique que el entrevistador haga lo que indica el guion. Cada respuesta está escrita para probar una cosa (respuesta completa, sin resultado, respuesta general…) en el nivel de inglés indicado.",
  englishLabel: "Nivel de inglés de las respuestas",
  english: { "": "Mixto", strong: "Avanzado", intermediate: "Intermedio", weak: "Básico" },
  level: { strong: "Inglés avanzado", intermediate: "Inglés intermedio", weak: "Inglés básico" },
  writeAgain: "Generar un nuevo guion",
  print: "Imprimir",
  writing: "Generando su guion… esto tarda unos 10–30 segundos.",
  writesLeft: (n) => (n === 1 ? "1 guion nuevo más para esta entrevista." : `${n} guiones nuevos más para esta entrevista.`),
  question: (n) => `Pregunta ${n}`,
  say: "Diga",
  expect: "El entrevistador debe",
  expectNone: "pasar a la siguiente pregunta (sin pregunta de seguimiento).",
  expectRepeat: "pedirle que repita:",
  expectFollowUp: "hacer una pregunta de seguimiento:",
  expectFollowUpAny: "hacer una pregunta de seguimiento.",
  note: "Es posible que esta respuesta todavía mencione el punto. Si no llega ninguna pregunta de seguimiento, intente omitirlo con más claridad.",
  followUpsOff: "Las preguntas de seguimiento están desactivadas en este momento, así que todas las respuestas simplemente pasarán a la siguiente pregunta.",
  cases: {
    complete: "Respuesta completa",
    no_result: "No dice cuál fue el resultado",
    hypothetical: "Respuesta general, sin un ejemplo real",
    no_example: "Dice que no tiene un ejemplo",
    generic: "Genérica, sin relación con este puesto",
    unclear: "Murmurada, difícil de entender",
    missing: "Omite:",
  },
  errors: {
    answer_script_off: "El guion de respuestas no está activado para su cuenta.",
    answer_script_limit: "Ya generó el número máximo de guiones para esta entrevista.",
    no_questions: "Las preguntas de esta entrevista aún no están listas. Inténtelo de nuevo en un momento.",
    other: "No pudimos generar el guion. Inténtelo de nuevo.",
  },
};

const FR = {
  button: "Script de réponses",
  buttonHint: "Des réponses à lire à voix haute pour chaque question, et ce que l’intervieweur devrait faire ensuite. S’ouvre dans un nouvel onglet.",
  title: "Script de réponses",
  intro: "Lisez à voix haute, en anglais, la réponse à chaque question, puis vérifiez que l’intervieweur fait ce que le script indique. Chaque réponse est écrite pour tester une chose (réponse complète, résultat manquant, réponse générale…) au niveau d’anglais indiqué.",
  englishLabel: "Niveau d’anglais des réponses",
  english: { "": "Mixte", strong: "Avancé", intermediate: "Intermédiaire", weak: "De base" },
  level: { strong: "Anglais avancé", intermediate: "Anglais intermédiaire", weak: "Anglais de base" },
  writeAgain: "Générer un nouveau script",
  print: "Imprimer",
  writing: "Rédaction de votre script… cela prend environ 10 à 30 secondes.",
  writesLeft: (n) => (n === 1 ? "Encore 1 nouveau script pour cette entrevue." : `Encore ${n} nouveaux scripts pour cette entrevue.`),
  question: (n) => `Question ${n}`,
  say: "Dites",
  expect: "L’intervieweur devrait",
  expectNone: "passer à la question suivante (sans question de suivi).",
  expectRepeat: "vous demander de répéter :",
  expectFollowUp: "poser une question de suivi :",
  expectFollowUpAny: "poser une question de suivi.",
  note: "Cette réponse touche peut-être encore le point. Si aucune question de suivi ne vient, essayez de l’omettre plus clairement.",
  followUpsOff: "Les questions de suivi sont désactivées pour le moment : chaque réponse passera donc simplement à la question suivante.",
  cases: {
    complete: "Réponse complète",
    no_result: "Ne dit pas quel a été le résultat",
    hypothetical: "Réponse générale, sans exemple réel",
    no_example: "Dit ne pas avoir d’exemple",
    generic: "Générale, sans lien avec ce poste",
    unclear: "Marmonnée, difficile à comprendre",
    missing: "Omet :",
  },
  errors: {
    answer_script_off: "Le script de réponses n’est pas activé pour votre compte.",
    answer_script_limit: "Vous avez généré le nombre maximal de scripts pour cette entrevue.",
    no_questions: "Les questions de cette entrevue ne sont pas encore prêtes. Réessayez dans un instant.",
    other: "Nous n’avons pas pu générer le script. Veuillez réessayer.",
  },
};

const ALL = { en: EN, "pt-BR": PT, "es-419": ES, "fr-CA": FR };

export function answerScriptStrings(lang) {
  return ALL[lang] || EN;
}

export const ANSWER_SCRIPT_LANGS = Object.keys(ALL);
